<template>
  <v-app>
    <div class="ofi-root" :class="`ofi-root--${screenMode}`" :style="themeVars">
      <header ref="header" class="ofi-header">
        <div class="ofi-header__text">
          <div v-if="dateLabel" class="ofi-header__eyebrow">
            {{ dateLabel }}
          </div>
          <h1 class="ofi-header__title">{{ headerTitle }}</h1>
        </div>
        <div v-if="logoUrl" class="ofi-header__logo">
          <img :src="logoUrl" alt="" />
        </div>
      </header>

      <main class="ofi-main">
        <div v-if="!paginatedProperties.length" class="ofi-empty">
          <div class="ofi-empty__text">
            {{ loading ? "Loading…" : "No inspections scheduled" }}
          </div>
        </div>
        <router-view
          v-else
          :properties="paginatedProperties"
          :screen-mode="screenMode"
        />
      </main>

      <footer
        ref="footer"
        class="ofi-footer"
        :style="{ visibility: pageCount > 1 ? 'visible' : 'hidden' }"
      >
        <span
          v-for="n in Math.max(pageCount, 1)"
          :key="n"
          class="ofi-dot"
          :class="{ 'ofi-dot--on': n === page }"
        ></span>
      </footer>
    </div>
  </v-app>
</template>

<script>
import { getProperties } from "./fns";
import dayjs from "dayjs";

const DEFAULTS = {
  background: "#0B1D3A",
  font1: "#F4F7FC",
  font2: "#E8B84B",
  fontFamily: '"Plus Jakarta Sans", "Montserrat", system-ui, sans-serif',
};

// Media-item keys recognised for each themeable value. The playlist may send
// camelCase or snake_case, so accept both; first key present wins.
const KEYS = {
  background: [
    "backgroundColor",
    "background_color",
    "bgColor",
    "bg_color",
    "background",
  ],
  font1: [
    "fontColor1",
    "font_color_1",
    "fontColor",
    "font_color",
    "textColor",
    "text_color",
  ],
  font2: [
    "fontColor2",
    "font_color_2",
    "accentColor",
    "accent_color",
    "highlightColor",
    "highlight_color",
  ],
  logo: ["logo", "logo_url", "logoUrl", "agency_logo", "agencyLogo", "image"],
  fontLink: ["fontLink", "font_link", "fontUrl", "font_url", "font"],
  fontFamily: ["fontFamily", "font_family", "fontName", "font_name"],
};

function pick(media, keys) {
  for (const key of keys) {
    const value = media?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "")
      return String(value).trim();
  }
  return "";
}

// Accepts "#0B1D3A", "0B1D3A", the 8-digit alpha forms, or any plain CSS
// colour ("rebeccapurple").
function normalizeColor(value) {
  if (!value) return "";
  return /^[0-9a-fA-F]{3,8}$/.test(value) && [3, 4, 6, 8].includes(value.length)
    ? `#${value}`
    : value;
}

// Pulls the family name out of a Google Fonts stylesheet URL so the caller
// only has to supply the link, e.g. ...css2?family=Inter:wght@400;700 -> Inter
function familyFromLink(link) {
  try {
    const params = new URL(link, window.location.href).searchParams;
    const family = params.getAll("family")[0];
    if (!family) return "";
    return family.split(":")[0].replace(/\+/g, " ").trim();
  } catch (e) {
    return "";
  }
}

const FONT_FILE_RE = /\.(woff2?|ttf|otf|eot)(\?|#|$)/i;

// Minimum comfortable row height per layout mode, in CSS px. Drives how many
// listings fit on a page for a given screen height.
const MIN_ROW_HEIGHT = { narrow: 155, compact: 190, wide: 118 };

// Handles #RGB, #RGBA, #RRGGBB and #RRGGBBAA — the CMS colour picker runs in
// "hexa" mode, so it sends the 8-digit form. Any alpha is dropped; it is only
// the base rgb that the derived tints below need.
function hexToRgb(hex) {
  let h = String(hex || "")
    .trim()
    .replace(/^#/, "");
  if (h.length === 3 || h.length === 4)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  if (!/^[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(h)) return null;
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

// The prototype leans on color-mix(), which many signage players are too old
// to support. Resolve the same values to plain rgba() up front instead.
function alpha(hex, a, fallback) {
  const c = hexToRgb(hex) || hexToRgb(fallback) || [255, 255, 255];
  return `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${a})`;
}

export default {
  name: "App",
  data() {
    return {
      media: {},
      loading: true,
      allProperties: [],
      page: 1,
      itemsPerPage: 6,
      screenMode: "wide",
      rowMin: MIN_ROW_HEIGHT.wide,
      pageTimer: null,
    };
  },
  created() {
    window.loadData = this.loadData;
    if (window.name) {
      const data = JSON.parse(window.name);
      if (data) this.loadData(data);
    }
    this.setGlobalFontColor(DEFAULTS.font1);
    this.setGlobalBackgroundColor(DEFAULTS.background);
    this.listToJS();
  },
  computed: {
    backgroundColor() {
      return (
        normalizeColor(pick(this.media, KEYS.background)) || DEFAULTS.background
      );
    },
    fontColor1() {
      return normalizeColor(pick(this.media, KEYS.font1)) || DEFAULTS.font1;
    },
    fontColor2() {
      return normalizeColor(pick(this.media, KEYS.font2)) || DEFAULTS.font2;
    },
    fontLink() {
      return pick(this.media, KEYS.fontLink);
    },
    fontFamily() {
      const explicit = pick(this.media, KEYS.fontFamily);
      let derived = "";
      if (this.fontLink) {
        derived = FONT_FILE_RE.test(this.fontLink)
          ? "OFI Custom Font" // matches the @font-face applyFontLink() writes
          : familyFromLink(this.fontLink);
      }
      const name = explicit || derived;
      // Keep the stock stack as fallback so a failed webfont still renders.
      return name ? `"${name}", ${DEFAULTS.fontFamily}` : DEFAULTS.fontFamily;
    },
    themeVars() {
      const bg = this.backgroundColor;
      const c2 = this.fontColor1;
      const c3 = this.fontColor2;
      return {
        "--c1": bg,
        "--c2": c2,
        "--c3": c3,
        "--c2-03": alpha(c2, 0.03, DEFAULTS.font1),
        "--c2-06": alpha(c2, 0.06, DEFAULTS.font1),
        "--c2-08": alpha(c2, 0.08, DEFAULTS.font1),
        "--c2-25": alpha(c2, 0.25, DEFAULTS.font1),
        "--c2-55": alpha(c2, 0.55, DEFAULTS.font1),
        "--c2-60": alpha(c2, 0.6, DEFAULTS.font1),
        "--c3-35": alpha(c3, 0.35, DEFAULTS.font2),
        "--row-min": `${this.rowMin}px`,
        "--row-max": `${Math.round(this.rowMin * 1.9)}px`,
        "--font-family": this.fontFamily,
      };
    },
    headerTitle() {
      return this.media.header || "Open for Inspection";
    },
    logoUrl() {
      return pick(this.media, KEYS.logo);
    },
    dateLabel() {
      if (!this.inspectionDate) return "";
      const d = dayjs(this.inspectionDate);
      return d.isValid()
        ? d.format("dddd, D MMMM YYYY")
        : String(this.inspectionDate);
    },
    properties() {
      const list = this.allProperties.filter(
        (item) =>
          item.inspectionDates &&
          item.inspectionDates.includes(this.inspectionDate) &&
          item.inspectionTimes.length > 0,
      );

      const inspectionDate = new Date(this.inspectionDate).toDateString();
      const filteredAndSorted = list
        .map((item) => {
          const matchingTimes = item.inspectionTimes.filter(
            (t) => new Date(t.startDateTime).toDateString() === inspectionDate,
          );
          return { ...item, inspectionTimes: matchingTimes };
        })
        .filter((item) => item.inspectionTimes.length > 0)
        .sort((a, b) => {
          const aTime = new Date(a.inspectionTimes[0].startDateTime).getTime();
          const bTime = new Date(b.inspectionTimes[0].startDateTime).getTime();
          return aTime - bTime;
        });

      if (!this.media?.listing_count?.length) return [];
      return filteredAndSorted.slice(
        this.media.listing_count[0],
        this.media.listing_count[1],
      );
    },
    inspectionDate() {
      let allDates = [];

      // First loop: Collect inspection dates
      for (const property of this.allProperties) {
        if (property.inspectionDates) {
          for (const dateStr of property.inspectionDates) {
            allDates.push({
              original: dateStr,
              parsed: dayjs(dateStr, "ddd, DD MMM YYYY"),
            });
          }
        }
      }

      // Second loop: Filter valid dates
      let validDates = [];
      for (const dateObj of allDates) {
        if (dateObj.parsed.isValid()) {
          validDates.push(dateObj);
        }
      }

      if (!validDates.length) return false;

      const today = dayjs();
      let futureDates = [];
      let pastDates = [];

      // Separate into future and past dates
      for (const dateObj of validDates) {
        if (dateObj.parsed.isAfter(today)) {
          futureDates.push(dateObj);
        } else if (dateObj.parsed.isBefore(today)) {
          pastDates.push(dateObj);
        }
      }

      if (futureDates.length) {
        return futureDates.sort((a, b) => a.parsed - b.parsed)[0].original;
      } else {
        return pastDates.sort((a, b) => b.parsed - a.parsed)[0].original;
      }
    },

    paginatedProperties() {
      const start = (this.page - 1) * this.itemsPerPage;
      const end = this.page * this.itemsPerPage;
      return this.properties.slice(start, end);
    },
    pageCount() {
      return Math.ceil(this.properties.length / this.itemsPerPage) || 0;
    },
  },

  watch: {
    // Re-measure once the header knows its final size (title/date/logo).
    headerTitle: "scheduleLayout",
    dateLabel: "scheduleLayout",
    logoUrl: "scheduleLayout",
    pageCount(count) {
      if (this.page > count) this.page = 1;
    },
  },

  methods: {
    testData() {
      const sample = {
        id: "1nNPu2F8fpGp3amyNCly",
        email: "nadine@beetek.com.au",
        brand: "next",
        header: "Open For Inspection",
        agency_ids: [
          "webcore-34",
          "ourproperty-MRCVIC23",
          "rex-00270",
          "vaultre-5404",
          "rex-00909",
          "agentbox-265",
          "agentbox-1106OBRNREBRWCK",
          "ourproperty-Beetek",
        ],
        listing_count: [0, 5],
        duration: 5,
        listing_type: ["rental"],
      };
      setTimeout(() => window.loadData(sample), 2000);
      window.localMediaData = sample;
    },
    // Adds the caller's webfont to <head>. Accepts either a stylesheet URL
    // (Google Fonts and friends) or a direct font-file URL, which gets wrapped
    // in an @font-face rule. Re-running replaces the previous element.
    applyFontLink() {
      const existing = document.getElementById("ofi-font");
      if (existing) existing.parentNode.removeChild(existing);

      const href = this.fontLink;
      if (!href) return;

      let el;
      if (FONT_FILE_RE.test(href)) {
        const name = pick(this.media, KEYS.fontFamily) || "OFI Custom Font";
        el = document.createElement("style");
        el.textContent = `@font-face { font-family: "${name}"; src: url("${href}"); font-display: swap; }`;
      } else {
        el = document.createElement("link");
        el.rel = "stylesheet";
        el.href = href;
      }
      el.id = "ofi-font";
      document.head.appendChild(el);

      // Metrics shift once the font lands, so re-measure the header.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.scheduleLayout);
      }
    },
    listToJS() {
      window.addEventListener(
        "message",
        function (event) {
          if (event.data) eval(event.data);
        },
        false,
      );
    },
    setGlobalBackgroundColor(color) {
      window.backgroundColor = color;
      this.$vuetify.theme.themes.light.background = color;
      this.$vuetify.theme.themes.dark.background = color;
    },
    setGlobalFontColor(color) {
      window.fontColor = color;
      this.$vuetify.theme.themes.light.primary = color;
      this.$vuetify.theme.themes.light.onBackground = color;
      this.$vuetify.theme.themes.light.color = color;
    },
    loadData(media) {
      this.media = typeof media === "object" ? media : JSON.parse(media);
      this.setGlobalBackgroundColor(this.backgroundColor);
      this.setGlobalFontColor(this.fontColor1);
      this.applyFontLink();
      this.scheduleLayout();
      this.fetchProperties();
    },

    // One request for every agency on the media, not one per agency: the old
    // loop was a round trip each, which is most of why a single board produced
    // hundreds of queries a day. The API also applies `status = current` and
    // drops listings with no inspection times, so nothing needs filtering back
    // out here - that work used to happen after paying to fetch it.
    async fetchProperties() {
      const agency_ids = this.media.agency_ids || [];
      const listing_type = this.media.listing_type || [];

      this.allProperties = await getProperties(agency_ids, listing_type);

      this.loading = false;
      this.scheduleLayout();
    },

    autoSwitchPage() {
      clearInterval(this.pageTimer);
      this.pageTimer = setInterval(() => {
        this.page = this.page < this.pageCount ? this.page + 1 : 1;
      }, (this.media?.duration || 30) * 1000);
    },

    scheduleLayout() {
      this.$nextTick(this.updateLayout);
    },

    // Picks the layout mode and how many listings fit on one page, so rows stay
    // legible from 535x960 strips up to 1920x1080 and 1080x1920 portrait.
    updateLayout() {
      const w = window.innerWidth;
      const h = window.innerHeight;

      const mode = w < 720 ? "narrow" : w < 1280 ? "compact" : "wide";
      this.screenMode = mode;
      this.rowMin = MIN_ROW_HEIGHT[mode];

      const headerH = this.$refs.header ? this.$refs.header.offsetHeight : 0;
      const footerH = this.$refs.footer ? this.$refs.footer.offsetHeight : 0;
      const padY = Math.min(44, Math.max(16, 0.012 * w + 0.012 * h)) * 2;
      const mainPadTop = Math.min(30, Math.max(12, 0.016 * h));
      const gap = Math.min(20, Math.max(8, 0.011 * h));

      const available = h - padY - headerH - footerH - mainPadTop;
      const fit = Math.floor((available + gap) / (this.rowMin + gap));

      this.itemsPerPage = Math.max(2, Math.min(12, fit));
      this.$nextTick(() => this.fitRows(8));
    },

    // The estimate above is deliberately optimistic. Long addresses and price
    // ranges can still push a row past its share of the screen, so shed one
    // listing at a time until every row renders without clipping.
    fitRows(attempts) {
      if (attempts <= 0 || this.itemsPerPage <= 2) return;
      const list = this.$el.querySelector(".ofi-list");
      if (!list) return;

      const rows = list.querySelectorAll(".ofi-row");
      if (rows.length < this.itemsPerPage) return; // not enough data to judge

      let clipped = list.scrollHeight > list.clientHeight + 1;
      for (const row of rows) {
        if (
          row.scrollHeight > row.clientHeight + 1 ||
          row.scrollWidth > row.clientWidth + 1
        ) {
          clipped = true;
          break;
        }
      }

      if (!clipped) return;
      this.itemsPerPage -= 1;
      this.$nextTick(() => this.fitRows(attempts - 1));
    },
  },
  mounted() {
    if (window.localMediaData) this.loadData(window.localMediaData);
    this.updateLayout();
    this.autoSwitchPage();
    window.addEventListener("resize", this.scheduleLayout);
    //this.testData();
  },
  beforeDestroy() {
    clearInterval(this.pageTimer);
    window.removeEventListener("resize", this.scheduleLayout);
  },
};
</script>

<style>
/* ---------------------------------------------------------------- header */
.ofi-header {
  flex: none;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px var(--gap-col);
  border-bottom: 2px solid var(--c3-35);
  padding-bottom: clamp(10px, 1.4vh, 26px);
}

.ofi-header__text {
  min-width: 0;
}

.ofi-header__eyebrow {
  font-size: var(--fs-eyebrow);
  font-weight: 700;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--c3);
  margin-bottom: 0.4em;
}

.ofi-header__title {
  margin: 0;
  font-size: var(--fs-title);
  font-weight: 800;
  line-height: 0.98;
  letter-spacing: -0.015em;
  text-wrap: balance;
}

.ofi-header__logo {
  margin-left: auto;
  display: flex;
  align-items: center;
  height: clamp(48px, 4vw + 4vh, 140px);
  width: clamp(120px, 10vw + 8vh, 380px);
  flex: none;
}

.ofi-header__logo img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  margin-left: auto;
}

/* On a narrow strip the logo gets its own line above the title. */
.ofi-root--narrow .ofi-header {
  flex-direction: column;
  align-items: stretch;
  gap: clamp(8px, 1.2vh, 18px);
}

.ofi-root--narrow .ofi-header__logo {
  order: -1;
  margin-left: 0;
  width: 100%;
  height: clamp(40px, 7vh, 96px);
}

.ofi-root--narrow .ofi-header__logo img {
  margin-left: 0;
}

/* ------------------------------------------------------------------ main */
.ofi-main {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding-top: clamp(12px, 1.6vh, 30px);
}

.ofi-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ofi-empty__text {
  font-size: var(--fs-street);
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--c2-55);
  text-align: center;
}

/* ---------------------------------------------------------------- footer */
.ofi-footer {
  flex: none;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: clamp(6px, 0.6vw, 12px);
  padding-top: clamp(8px, 1vh, 18px);
  height: clamp(20px, 2.4vh, 34px);
  box-sizing: content-box;
}

.ofi-dot {
  width: clamp(6px, 0.5vw, 11px);
  height: clamp(6px, 0.5vw, 11px);
  border-radius: 50%;
  background: var(--c2-25);
  transition: background 0.3s ease;
}

.ofi-dot--on {
  background: var(--c3);
}
</style>
